import { useManager } from 'dreamstate';
import { useEffect } from 'react';

import { Flex, Header, rem, SystemStyleObject } from '@vimeo/bokeh';
import { ChartGrowthAlt } from '@vimeo/bokeh-icons';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { translations } from '@vimeo/cloud-live/translations';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Maybe } from '@vimeo/core/types/generic';
import { BlockingLoadingWrapper } from '@vimeo/live-core/components/loading';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { IPollContext, PollManager } from '@vimeo/live-core/store/firebase/interaction/poll';
import { trackViewPoll } from '@vimeo/live-core/tracking/tracking_engagement_polls';
import { trackVenuesViewPoll } from '@vimeo/live-core/tracking/tracking_venues_engagement';
import { EComposerSessionType } from '@vimeo/live-core/types/composer_session';
import { EPollState, IPoll } from '@vimeo/live-core/types/interaction_tools';

import { PollResults } from './PollResults';
import { PollVote } from './PollVote';

const POLL_ITEM_WRAPPER_STYLE: SystemStyleObject = {
  position: 'relative',
  width: '100%',
  height: '100%',
  padding: rem(24),
  flexDirection: 'column',
  alignSelf: 'flex-start',
  rowGap: rem(16),
  overflow: 'auto',
};

interface IPollViewProps extends IBaseDomElementProps {
  pollContext?: IPollContext;
}

export function PollView({
  id = createInteractionDomName('poll-view'),
  // eslint-disable-next-line react-hooks/rules-of-hooks
  pollContext: { isHydrated, polls, activePollId, pollActions } = useManager(PollManager),
}: IPollViewProps) {
  const {
    initialState: { sessionType },
  } = useLiveGlobals();
  const activePoll: Maybe<IPoll> = activePollId ? polls[activePollId] : null;

  useEffect(() => {
    trackViewPoll();
  }, []);

  useEffect(() => {
    if (isHydrated && activePollId && sessionType === EComposerSessionType.VENUE) {
      trackVenuesViewPoll(String(activePollId));
    }
  }, [activePollId, isHydrated, sessionType]);

  if (!isHydrated) {
    return (
      <Flex id={id} sx={POLL_ITEM_WRAPPER_STYLE}>
        <BlockingLoadingWrapper opacity={0.1} withAnimation={true} />
      </Flex>
    );
  } else if (!activePoll) {
    return (
      <Flex
        alignItems={'center'}
        justifyContent={'center'}
        flexDirection={'column'}
        margin={'auto'}
      >
        <ChartGrowthAlt boxSize={'lg'} />

        <Flex
          id={createInteractionDomName(id, 'placeholder')}
          alignItems={'center'}
          justifyContent={'center'}
          height={'100%'}
          color={'text-secondary'}
          textAlign={'center'}
          flexGrow={1}
          margin={`${rem(16)} 0`}
        >
          {translations.noPollCurrentlyAvailable}
        </Flex>
      </Flex>
    );
  }

  const isVotedOption: boolean = activePoll.options.some((option) => option.isVotedByMe);
  const pollOpened: boolean = activePoll.state === EPollState.OPENED;

  return (
    <Flex id={id} sx={POLL_ITEM_WRAPPER_STYLE}>
      <Header
        id={createInteractionDomName(id, 'question')}
        size={'sm'}
        margin={0}
        sx={{
          wordWrap: 'break-word',
        }}
      >
        {activePoll.question}
      </Header>

      {pollOpened ? (
        <PollVote
          id={createInteractionDomName(id, 'votes')}
          poll={activePoll}
          onSetVote={pollActions.voteForPollOption}
          isVotedOption={isVotedOption}
        />
      ) : (
        <PollResults id={createInteractionDomName(id, 'results')} poll={activePoll} />
      )}
    </Flex>
  );
}
